'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { 
  ShieldCheck, ArrowRight, Linkedin, Instagram, 
  Youtube, Phone, Mail, Menu, X, ChevronRight 
} from 'lucide-react';

export default function PrivacyPolicyPage() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState('');
  const [currentYear, setCurrentYear] = useState('2026');

  // Garante que a data só será renderizada no lado do cliente,
  // evitando o erro de Hydration (incompatibilidade Servidor x Cliente)
  useEffect(() => {
    setCurrentDate(new Date().toLocaleDateString('pt-BR'));
    setCurrentYear(new Date().getFullYear().toString());
  }, []);

  const wppLink = "https://wa.me/554888796761?text=Quero%20falar%20com%20um%20especialista%20sobre%20o%20sistema%20Nexus%20Safe.";
  const appLink = "https://app.nexussafe.com.br/";

  return (
    <div className="min-h-screen bg-white selection:bg-blue-200 selection:text-blue-900 font-sans text-gray-800 flex flex-col relative overflow-x-hidden">
      
      {/* ================= NAVBAR ================= */}
      <nav className="fixed w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-100 shadow-sm top-0">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between relative">
          <div className="flex items-center shrink-0 h-full">
            <Link href="/" className="flex items-center">
              <img 
                src="/logo-nexus.png" 
                alt="NexusSafe" 
                style={{ height: '40px', width: 'auto' }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if(fallback) fallback.style.display = 'flex';
                }}
              />
              <div style={{ display: 'none' }} className="items-center gap-2">
                <ShieldCheck size={28} className="text-gray-900" />
                <span className="text-xl font-black text-gray-900 tracking-tight">NEXUS<span className="font-light">SAFE</span></span>
              </div>
            </Link>
          </div>
          
          <div className="hidden md:flex items-center gap-4 lg:gap-8 font-bold text-sm text-gray-600">
            <Link href="/site/solucoes" className="hover:text-blue-600 transition-colors">Soluções</Link>
            <Link href="/site/para-quem-e" className="hover:text-blue-600 transition-colors">Para quem é</Link>
            <Link href="/site/recursos" className="hover:text-blue-600 transition-colors">Recursos</Link>
            <Link href="/site/conteudos" className="hover:text-blue-600 transition-colors">Conteúdos</Link>
            <Link href="/site/sobre-nos" className="hover:text-blue-600 transition-colors">Sobre nós</Link>
          </div>

          <div className="flex items-center gap-4 text-sm font-bold shrink-0">
            <div className="hidden md:flex items-center gap-4">
              <a href={appLink} className="text-gray-800 hover:text-blue-600 transition-colors border-r border-gray-300 pr-4">Entrar</a>
              <a href={wppLink} target="_blank" rel="noopener noreferrer" className="bg-blue-600 text-white px-5 py-2.5 rounded-full hover:bg-blue-700 transition-all flex items-center gap-2 shadow-lg shadow-blue-600/20 whitespace-nowrap">
                Fale com um especialista <ArrowRight size={16} />
              </a>
            </div>
            {/* Ícone Menu Mobile - Só aparece no celular (md:hidden) */}
            <button 
              className="md:hidden text-gray-800 p-2 transition-colors hover:text-blue-600"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Alternar menu"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* ================= MENU MOBILE (DROPDOWN) ================= */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 w-full bg-white shadow-xl border-t border-gray-100 flex flex-col p-6 gap-4 z-40 max-h-[calc(100vh-80px)] overflow-y-auto">
            <Link href="/site/solucoes" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Soluções <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link href="/site/para-quem-e" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Para quem é <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link href="/site/recursos" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Recursos <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link href="/site/conteudos" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Conteúdos <ChevronRight size={16} className="text-gray-400" />
            </Link>
            <Link href="/site/sobre-nos" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-800 font-bold hover:text-blue-600 py-3 border-b border-gray-50 flex justify-between items-center">
              Sobre nós <ChevronRight size={16} className="text-gray-400" />
            </Link>
            
            <div className="flex flex-col gap-3 mt-4">
              <a href={appLink} className="w-full text-center text-gray-900 font-bold py-3.5 border-2 border-gray-200 rounded-full hover:bg-gray-50 transition-colors">
                Entrar no Sistema
              </a>
              <a href={wppLink} target="_blank" rel="noopener noreferrer" className="w-full bg-blue-600 text-white py-3.5 rounded-full font-bold hover:bg-blue-700 transition-all flex justify-center items-center gap-2 shadow-lg shadow-blue-600/20">
                Fale com um especialista <ArrowRight size={16} />
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ================= CONTEÚDO ================= */}
      <main className="flex-grow pt-32 pb-24 px-6 max-w-4xl mx-auto w-full">
        <div className="mb-10 border-b border-gray-100 pb-8 mt-10 md:mt-0">
          <h1 className="text-4xl font-black text-gray-900 tracking-tight mb-4">Política de Privacidade</h1>
          <p className="text-gray-500 font-medium">
            Última atualização: {currentDate ? currentDate : 'Carregando...'}
          </p>
        </div>

        <div className="space-y-8 text-gray-600 leading-relaxed font-medium">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introdução</h2>
            <p>
              A <strong>NexusSafe</strong> está comprometida em proteger a sua privacidade. Esta Política de Privacidade 
              explica como coletamos, usamos, divulgamos e protegemos suas informações quando você visita nosso site 
              institucional ou utiliza nossa plataforma SaaS de Saúde e Segurança do Trabalho (SST), em conformidade 
              com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Dados que Coletamos</h2>
            <p className="mb-3">Coletamos informações pessoais e profissionais necessárias para a prestação dos nossos serviços:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Dados de Conta (Empresas):</strong> Nome, e-mail, telefone, cargo, CNPJ e dados de faturamento.</li>
              <li><strong>Dados de Colaboradores (Inseridos por clientes):</strong> Nome, CPF, cargo, setor e respostas a questionários de saúde ocupacional (EET, Canal de Denúncia, etc.), que podem conter dados sensíveis. A NexusSafe atua apenas como <em>Operadora</em> destes dados.</li>
              <li><strong>Dados de Navegação:</strong> Endereço IP, tipo de navegador, páginas visitadas e cookies (consulte nossa <Link href="/site/cookies" className="text-blue-600 hover:underline">Política de Cookies</Link>).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Como Usamos as Informações</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Para fornecer, operar e manter a Plataforma NexusSafe.</li>
              <li>Para processar pagamentos e enviar comunicações administrativas (faturas, atualizações, alertas de segurança).</li>
              <li>Para gerar relatórios e matrizes de risco psicossocial de forma estatística e anonimizada.</li>
              <li>Para cumprir obrigações legais ou regulatórias.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Compartilhamento de Dados</h2>
            <p>
              Não vendemos ou alugamos seus dados pessoais a terceiros. Os dados podem ser compartilhados exclusivamente com:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Fornecedores de serviços de infraestrutura (ex: servidores em nuvem, gateways de pagamento) sob rigorosos acordos de confidencialidade.</li>
              <li>Autoridades legais, quando exigido por lei ou ordem judicial.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Segurança dos Dados</h2>
            <p>
              Implementamos medidas técnicas e organizacionais de segurança com padrão de mercado (incluindo criptografia ponta a ponta e controles de acesso rigorosos) para proteger suas informações pessoais e os dados sensíveis dos seus colaboradores contra acesso não autorizado, alteração ou destruição.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Seus Direitos (LGPD)</h2>
            <p className="mb-3">Você tem o direito de:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Confirmar a existência de tratamento de dados.</li>
              <li>Acessar seus dados pessoais.</li>
              <li>Corrigir dados incompletos, inexatos ou desatualizados.</li>
              <li>Solicitar a anonimização, bloqueio ou eliminação de dados desnecessários.</li>
              <li>Revogar o consentimento a qualquer momento.</li>
            </ul>
            <p className="mt-4">
              Para exercer seus direitos, os colaboradores devem entrar em contato diretamente com a Empresa Contratante (Controladora dos dados). Administradores de contas podem contatar a NexusSafe via e-mail.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Contato</h2>
            <p>
              Se você tiver dúvidas sobre esta Política de Privacidade ou sobre como tratamos seus dados, entre em contato com nosso Encarregado de Proteção de Dados (DPO) através do e-mail: <a href="mailto:comercial@nexussafe.com.br" className="text-blue-600 hover:underline">comercial@nexussafe.com.br</a>.
            </p>
          </section>
        </div>
      </main>

      {/* ================= FOOTER ================= */}
      <footer style={{ backgroundColor: '#020617', paddingTop: '100px', paddingBottom: '40px' }} className="font-medium mt-auto">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-12 border-b border-gray-800 pb-16">
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center mb-6">
              <Link href="/" className="flex items-center">
                <img 
                  src="/logo-nexus-branca.png" 
                  alt="NexusSafe" 
                  style={{ height: '32px', width: 'auto', objectFit: 'contain' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if(fallback) fallback.style.display = 'flex';
                  }}
                />
                <div style={{ display: 'none' }} className="items-center gap-2">
                  <ShieldCheck size={28} className="text-white" />
                  <span className="text-xl font-black text-white tracking-tight">NEXUSSAFE</span>
                </div>
              </Link>
            </div>
            <p style={{ color: '#9CA3AF' }} className="text-sm leading-relaxed mb-8">
              A plataforma inteligente que simplifica a gestão de Segurança e Saúde do Trabalho e conecta pessoas, dados e resultados.
            </p>
            <div className="flex gap-4">
              <a href="#" style={{ backgroundColor: '#111827' }} className="w-10 h-10 rounded-full text-gray-400 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"><Linkedin size={18} /></a>
              <a href="#" style={{ backgroundColor: '#111827' }} className="w-10 h-10 rounded-full text-gray-400 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"><Instagram size={18} /></a>
              <a href="#" style={{ backgroundColor: '#111827' }} className="w-10 h-10 rounded-full text-gray-400 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"><Youtube size={18} /></a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Soluções</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm">
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Psicossocial + Plano</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Canal de Denúncia</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Checklist</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Plano de Ação</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Entrega de EPI</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">Questionários</Link></li>
              <li><Link href="/site/solucoes" className="hover:text-white transition-colors">CIPA</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Para quem é</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm">
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Indústrias</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Construtoras</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Comércio</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Serviços</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Saúde</Link></li>
              <li><Link href="/site/para-quem-e" className="hover:text-white transition-colors">Educação</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Recursos</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm mb-8">
              <li><Link href="/site/recursos" className="hover:text-white transition-colors">Blog</Link></li>
              <li><Link href="/site/recursos" className="hover:text-white transition-colors">Materiais</Link></li>
              <li><Link href="/site/recursos" className="hover:text-white transition-colors">Webinars</Link></li>
              <li><Link href="/site/recursos" className="hover:text-white transition-colors">Cases</Link></li>
            </ul>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Sobre Nós</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm">
              <li><Link href="/site/sobre-nos" className="hover:text-white transition-colors">Institucional</Link></li>
              <li><Link href="/site/sobre-nos" className="hover:text-white transition-colors">Parceiros</Link></li>
              <li><Link href="/site/sobre-nos" className="hover:text-white transition-colors">Contato</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold text-sm mb-5 uppercase">Fale Conosco</h4>
            <ul style={{ color: '#9CA3AF' }} className="space-y-3 text-sm mb-6">
              <li className="flex items-center gap-3"><Phone size={14} className="shrink-0"/> (48) 9.8879-6761</li>
              <li className="flex items-center gap-3"><Mail size={14} className="shrink-0"/> comercial@nexussafe.com.br</li>
            </ul>
            <a href={wppLink} target="_blank" rel="noopener noreferrer" className="inline-flex bg-blue-600 text-white px-5 py-2.5 rounded-full font-bold text-xs hover:bg-blue-700 transition-all items-center gap-2">
              Fale com um especialista <ArrowRight size={14} />
            </a>
          </div>
        </div>

        <div style={{ color: '#9CA3AF' }} className="max-w-7xl mx-auto px-6 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs">
          <p className="text-center md:text-left">© {currentYear} NexusSafe - Todos os direitos reservados</p>
          <div className="flex items-center gap-6">
            <Link href="/site/privacidade" className="text-blue-500 hover:text-white transition-colors whitespace-nowrap">Política de Privacidade</Link>
            <Link href="/site/termos" className="text-blue-500 hover:text-white transition-colors whitespace-nowrap">Termos de Uso</Link>
            <Link href="/site/cookies" className="text-blue-500 hover:text-white transition-colors whitespace-nowrap">Política de Cookies</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}